/**
 * Shared filter utilities for Algolia configuration
 * Used by all product search configs
 */

/**
 * Safely parse JSON string
 * @param {string} str - JSON string to parse
 * @returns {Object} Parsed object or empty object on error
 */
export function parseJson(str) {
  try {
    return JSON.parse(str);
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return {};
  }
}

/**
 * Escape a value for use in Algolia filter strings
 * Escapes backslashes and single quotes to prevent injection
 * @param {string} value - The value to escape
 * @returns {string} Escaped value safe for filter strings
 */
function escapeFilterValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Build Algolia filter string from object
 * @param {Object} filters - Object with facet keys and values
 * @returns {string} Algolia filter string
 *
 * @example
 * buildFilterString({ industries: ['Automotive', 'Aerospace'] })
 * // Returns: "(industries:'Automotive' OR industries:'Aerospace')"
 *
 * buildFilterString({ legacy: false })
 * // Returns: "legacy:'false'"
 */
function buildFilterString(filters) {
  const parts = Object.entries(filters)
    .filter(
      ([, values]) =>
        values && (Array.isArray(values) ? values.length > 0 : true),
    )
    .map(([key, values]) => {
      if (Array.isArray(values)) {
        const orParts = values
          .map((value) => `${key}:'${escapeFilterValue(value)}'`)
          .join(' OR ');
        return `(${orParts})`;
      }
      return `${key}:'${escapeFilterValue(values)}'`;
    });

  return parts.join(' AND ');
}

/**
 * Build the complete filter string including site filter
 * @param {string} siteId - Current site ID
 * @param {Object} customFilters - Additional filters from data attribute
 * @returns {string} Complete Algolia filter string
 */
export function buildCompleteFilterString(siteId, customFilters = {}) {
  const filterParts = [];

  // Always filter by current site and only show live, site-enabled entries
  if (siteId) {
    filterParts.push(`siteId:${siteId}`);
  }
  filterParts.push("status:'live'");
  filterParts.push('enabledForSite:true');

  // Add any custom filters
  const customFilterString = buildFilterString(customFilters);
  if (customFilterString) {
    filterParts.push(customFilterString);
  }

  return filterParts.join(' AND ');
}
